import { useState, useRef, useEffect, useLayoutEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { countryInfo } from '@/lib/country-flags';
import { CountryFlag } from './CountryFlag';

interface CountryPopoverProps {
  /** Team/country name as it appears in event data (e.g. "Mexico"). */
  name: string | null | undefined;
  /** Trigger content (e.g. flag + abbreviation). */
  children: ReactNode;
  className?: string;
}

/**
 * Wraps a team label in a clickable trigger that opens a small popover with the
 * country flag + info. For names that aren't recognized countries it renders the
 * children as plain inline content (no button, no popover) — so it's safe to use
 * around any team label regardless of sport.
 *
 * The popover renders into a portal with fixed positioning so it can't be
 * clipped by ancestor `overflow-hidden` containers (e.g. the game card).
 */
export function CountryPopover({ name, children, className }: CountryPopoverProps) {
  const info = countryInfo(name);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const updateCoords = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 8, left: r.left + r.width / 2 });
  };

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const reposition = () => updateCoords();
    const onPointerDown = (e: MouseEvent) => {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        popoverRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!info) {
    return <span className={cn('inline-flex items-center gap-1.5', className)}>{children}</span>;
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-label={`${info.name} info`}
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-1.5 cursor-pointer transition-colors hover:text-gold focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50 rounded',
          open && 'text-gold',
          className,
        )}
      >
        {children}
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              transform: 'translateX(-50%)',
              zIndex: 1000,
            }}
            className="w-52 rounded-lg bg-surface-3 border border-surface-4 shadow-xl p-3 text-left"
          >
            <div className="flex items-center gap-2.5">
              <CountryFlag name={info.name} size="lg" />
              <div className="min-w-0">
                <p className="text-cream font-serif font-bold text-sm leading-tight truncate">{info.name}</p>
                <p className="text-warm-gray/60 text-[10px] font-bold uppercase tracking-wider">{info.confederation}</p>
              </div>
            </div>
            <div className="mt-2.5 pt-2.5 border-t border-surface-4/60 space-y-1.5 text-[11px]">
              <div className="flex items-center justify-between">
                <span className="text-warm-gray/70">FIFA Ranking</span>
                <span className="text-gold font-bold">#{info.fifaRank}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-warm-gray/70">Confederation</span>
                <span className="text-cream/90 font-medium">{info.confederation}</span>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
